import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  TextField,
  IconButton,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import { Add, Edit, Delete, Save, Cancel, Upload } from '@mui/icons-material';
import AppService from '../../../services/AppService';
import { showNotification } from '../../../utils/notifications';
import { showSuccessMessage, showErroMessage } from '../../../LayoutContainers/AppLayout/redux/AppSlice';

// Helper to decode JWT and extract roles
const getTokenInfo = () => {
  try {
    const authInfo = localStorage.getItem('authInfo');
    const token = authInfo ? JSON.parse(authInfo)?.token : null;
    if (!token) return null;
    const [, payloadB64] = token.split('.') || [];
    if (!payloadB64) return null;
    const payload = JSON.parse(atob(payloadB64));
    return {
      roles: (payload?.roles || []).map(r => r.authority || r).concat(payload?.authorities || [])
    };
  } catch (e) {
    return null;
  }
};

const DepartmentsSettings = ({ organizationId }) => {
  const dispatch = useDispatch();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [newDepartmentName, setNewDepartmentName] = useState('');
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState(null);
  
  // Import departments states
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importableDepartments, setImportableDepartments] = useState([]);
  const [selectedImportDepartments, setSelectedImportDepartments] = useState([]);
  const [loadingImportable, setLoadingImportable] = useState(false);
  const [importing, setImporting] = useState(false);
  
  // Check if user is ROOT admin
  const tokenInfo = getTokenInfo();
  const isRootAdmin = tokenInfo?.roles?.includes('ROLE_ROOT_ADMIN') || false;

  // Fetch departments from organization config
  useEffect(() => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    fetchDepartments();
  }, [organizationId]);

  const fetchDepartments = async () => {
    try {
      setLoading(true);
      const response = await AppService.get(`/v1/organization-config/organization/${organizationId}`);
      setDepartments(response?.departments || []);
      setError('');
    } catch (err) {
      console.error('Error fetching departments:', err);
      setError('Failed to load departments');
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  };

  // Add new department
  const handleAddDepartment = () => {
    if (!newDepartmentName.trim()) {
      showNotification('Department name cannot be empty', 'error');
      return;
    }

    // Check for duplicates
    if (departments.some(d => d.departmentName.toLowerCase() === newDepartmentName.trim().toLowerCase())) {
      showNotification('Department already exists', 'error');
      return;
    }

    const newDepartment = {
      id: `temp-${Date.now()}`, // Temporary ID for frontend
      departmentName: newDepartmentName.trim(),
    };

    setDepartments([...departments, newDepartment]);
    setNewDepartmentName('');
    showNotification('Department added. Click Save to apply changes.', 'info');
  };

  // Start editing a department
  const handleEditClick = (department) => {
    setEditingId(department.id);
    setEditingName(department.departmentName);
  };

  // Save edited department
  const handleEditSave = (id) => {
    if (!editingName.trim()) {
      showNotification('Department name cannot be empty', 'error');
      return;
    }

    // Check for duplicates (excluding current)
    if (departments.some(d => d.id !== id && d.departmentName.toLowerCase() === editingName.trim().toLowerCase())) {
      showNotification('Department already exists', 'error');
      return;
    }

    setDepartments(departments.map(d =>
      d.id === id ? { ...d, departmentName: editingName.trim() } : d
    ));
    setEditingId(null);
    setEditingName('');
    showNotification('Department updated. Click Save to apply changes.', 'info');
  };

  // Cancel editing
  const handleEditCancel = () => {
    setEditingId(null);
    setEditingName('');
  };

  // Delete department
  const handleDeleteClick = (department) => {
    setDepartmentToDelete(department);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    setDepartments(departments.filter(d => d.id !== departmentToDelete.id));
    setDeleteDialogOpen(false);
    setDepartmentToDelete(null);
    showNotification('Department removed. Click Save to apply changes.', 'info');
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDepartmentToDelete(null);
  };

  // Fetch importable departments
  const fetchImportableDepartments = async () => {
    try {
      setLoadingImportable(true);
      const response = await AppService.get('/v1/departments/importable');
      setImportableDepartments(response?.departments || []);
      setSelectedImportDepartments([]);
      setError('');
    } catch (err) {
      console.error('Error fetching importable departments:', err);
      showNotification('Failed to load importable departments', 'error');
      setImportableDepartments([]);
    } finally {
      setLoadingImportable(false);
    }
  };

  // Open import dialog
  const handleOpenImportDialog = async () => {
    setImportDialogOpen(true);
    await fetchImportableDepartments();
  };

  // Close import dialog
  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
    setImportableDepartments([]);
    setSelectedImportDepartments([]);
  };

  // Toggle department selection
  const handleToggleDepartment = (departmentName) => {
    setSelectedImportDepartments(prev => {
      if (prev.includes(departmentName)) {
        return prev.filter(d => d !== departmentName);
      } else {
        return [...prev, departmentName];
      }
    });
  };

  // Toggle select all departments
  const handleToggleSelectAll = () => {
    if (selectedImportDepartments.length === importableDepartments.length) {
      setSelectedImportDepartments([]);
    } else {
      setSelectedImportDepartments([...importableDepartments]);
    }
  };

  // Import selected departments
  const handleImportDepartments = async () => {
    if (selectedImportDepartments.length === 0) {
      showNotification('Please select at least one department to import', 'warning');
      return;
    }

    try {
      setImporting(true);
      const response = await AppService.post(
        `/v1/organizations/${organizationId}/departments/import`,
        { departments: selectedImportDepartments }
      );

      // Update departments list with response data
      if (response?.departments) {
        setDepartments(response.departments);
        dispatch(showSuccessMessage(
          `Successfully imported ${selectedImportDepartments.length} department(s)`
        ));
        handleCloseImportDialog();
      } else {
        dispatch(showErroMessage('Departments imported but no data returned'));
        handleCloseImportDialog();
      }
    } catch (err) {
      console.error('Error importing departments:', err);
      // Handle different error response structures
      const errorMessage = err?.response?.data?.message || 
                          err?.message || 
                          err?.data?.message ||
                          'Failed to import departments';
      dispatch(showErroMessage(errorMessage));
    } finally {
      setImporting(false);
    }
  };

  // Save all changes to backend
  const handleSaveAll = async () => {
    if (!organizationId) {
      showNotification('Organization ID is missing', 'error');
      return;
    }

    setSaving(true);
    try {
      // Remove temporary IDs and send to backend in required format
      const departmentsToSave = departments.map(({ id, departmentName }) => ({
        ...(id && !id.toString().startsWith('temp-') ? { id } : {}),
        departmentName
      }));

      // Send with departments key as required
      await AppService.put(
        `/v1/organization-config/departments/organization/${organizationId}`,
        { departments: departmentsToSave }
      );

      showNotification('Departments saved successfully', 'success');
      // Refresh to get server-assigned IDs
      await fetchDepartments();
    } catch (err) {
      console.error('Error saving departments:', err);
      showNotification(err?.response?.data?.message || 'Failed to save departments', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!organizationId) {
    return (
      <Alert severity="warning">
        Please save the organization configuration first before managing departments.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
            Manage Departments
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Add, edit, or remove departments for your organization. Click Save to apply changes.
          </Typography>
        </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Upload />}
            onClick={handleOpenImportDialog}
            sx={{ px: 3, py: 1 }}
          >
            Import
          </Button>
      </Box>

      {/* Add new department */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: 'background.default', borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs>
            <TextField
              fullWidth
              label="New Department Name"
              value={newDepartmentName}
              onChange={(e) => setNewDepartmentName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddDepartment();
                }
              }}
              placeholder="e.g., Human Resources, Finance, IT"
              variant="outlined"
              size="medium"
            />
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddDepartment}
              disabled={!newDepartmentName.trim()}
              sx={{ height: 56, px: 3 }}
            >
              Add Department
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* List of departments */}
      {departments.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No departments added yet. Add your first department above.
        </Alert>
      ) : (
        <Paper elevation={2} sx={{ borderRadius: 2, border: '1px solid #ddd', overflow: 'hidden', mt: 2, backgroundColor: '#fff',}}>
          <List sx={{p: 1 }}>
            {departments.map((department, index) => (
              <ListItem
                key={department.id}
                divider={index < departments.length - 1}
                sx={{
                  px: 2,
                  py: 1.5,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                {editingId === department.id ? (
                  <>
                    <TextField
                      fullWidth
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleEditSave(department.id);
                        }
                      }}
                      autoFocus
                      size="small"
                    />
                    <ListItemSecondaryAction sx={{ right: 16 }}>
                      <IconButton
                        edge="end"
                        onClick={() => handleEditSave(department.id)}
                        color="primary"
                        sx={{ mr: 1 }}
                      >
                        <Save />
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={handleEditCancel}
                      >
                        <Cancel />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </>
                ) : (
                  <>
                    <ListItemText
                      primary={
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {department.departmentName}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction sx={{ right: 16 }}>
                      <IconButton
                        edge="end"
                        onClick={() => handleEditClick(department)}
                        color="primary"
                        sx={{ mr: 1 }}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={() => handleDeleteClick(department)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </>
                )}
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {/* Save button */}
      <Box mt={4} display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          startIcon={<Save />}
          onClick={handleSaveAll}
          disabled={saving || departments.length === 0}
          size="large"
          sx={{ px: 4, py: 1.5, fontWeight: 600 }}
        >
          {saving ? 'Saving...' : 'Save Departments'}
        </Button>
      </Box>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the department "{departmentToDelete?.departmentName}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import departments dialog */}
      <Dialog 
        open={importDialogOpen} 
        onClose={handleCloseImportDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Upload sx={{ mr: 1 }} />
            Import Departments
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingImportable ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : importableDepartments.length === 0 ? (
            <Alert severity="info">
              No departments available to import.
            </Alert>
          ) : (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Select the departments you want to import:
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedImportDepartments.length === importableDepartments.length && importableDepartments.length > 0}
                      indeterminate={selectedImportDepartments.length > 0 && selectedImportDepartments.length < importableDepartments.length}
                      onChange={handleToggleSelectAll}
                    />
                  }
                  label="Select All"
                />
              </Box>
              <FormGroup>
                {importableDepartments.map((departmentName) => (
                  <FormControlLabel
                    key={departmentName}
                    control={
                      <Checkbox
                        checked={selectedImportDepartments.includes(departmentName)}
                        onChange={() => handleToggleDepartment(departmentName)}
                      />
                    }
                    label={departmentName}
                  />
                ))}
              </FormGroup>
              {selectedImportDepartments.length > 0 && (
                <Typography variant="caption" color="primary" sx={{ mt: 2, display: 'block' }}>
                  {selectedImportDepartments.length} department(s) selected
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportDialog} disabled={importing}>
            Cancel
          </Button>
          <Button 
            onClick={handleImportDepartments} 
            variant="contained"
            disabled={importing || selectedImportDepartments.length === 0}
            startIcon={importing ? <CircularProgress size={20} /> : <Upload />}
          >
            {importing ? 'Importing...' : 'Import Selected'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DepartmentsSettings;
